import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'

/**
 * Height that makes the wizard end exactly at the viewport bottom, so the
 * footer (step rail + actions) is always on screen without page scrolling.
 * The app chrome above (top bar, banners, page padding) and below (page
 * bottom padding) differs per app and per breakpoint, so it can't be a
 * constant — measure the shell's own document offsets instead. Both values
 * are invariant to the shell's own height, so this doesn't feed back.
 */
function useViewportFitHeight(ref: React.RefObject<HTMLElement>): number | null {
  const [height, setHeight] = useState<number | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const above = el.getBoundingClientRect().top + window.scrollY
      // In-flow content after the shell: following siblings plus every
      // ancestor's bottom padding/border, up to <body>. (scrollHeight can't be
      // used — it's clamped to the viewport when the page is short, which
      // would stop the wizard from growing again on window enlarge.)
      let below = 0
      for (let node: HTMLElement = el; node.parentElement && node !== document.body; node = node.parentElement) {
        for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
          const cs = getComputedStyle(sib)
          if (cs.position !== 'fixed' && cs.position !== 'absolute') {
            below += (sib as HTMLElement).getBoundingClientRect().height
          }
        }
        const pcs = getComputedStyle(node.parentElement)
        below += (parseFloat(pcs.paddingBottom) || 0) + (parseFloat(pcs.borderBottomWidth) || 0)
      }
      setHeight(Math.round(window.innerHeight - above - below))
    }
    // Measure after layout settles (the chrome above can rewrap during a
    // resize), and re-measure on any body size change — that also covers
    // banner wrapping and font loading. Our own height update re-fires the
    // observer once with an identical result, so it settles immediately.
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('resize', schedule)
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    observer?.observe(document.body)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      observer?.disconnect()
    }
  }, [ref])
  return height
}

export interface PlanHuntWizardStep {
  id: string
  label: string
  /** Full descriptive title shown in the modal header, e.g. "When and where". Falls back to label. */
  title?: string
  /**
   * Sub-heading shown under the title, e.g. "Pick the team and when the hunt
   * runs" — the app-wide "big headline + smaller sub-heading" pairing.
   */
  description?: string
  state: 'locked' | 'pending' | 'active' | 'done'
  content: ReactNode
  canContinue: boolean
}

export interface PlanHuntWizardShellProps {
  steps: PlanHuntWizardStep[]
  currentStepId: string
  /** Jump to any step via the step rail. Omit to disable rail navigation. */
  onStepSelect?: (index: number) => void
  onBack: () => void
  onContinue: () => void
  onCancel: () => void
  onSaveDraft: () => void
  onFinalize?: () => void
  saving?: boolean
  /** When false, Save draft is disabled regardless of `saving`. Default true. */
  canSaveDraft?: boolean
  /** When false, Save & send invitations is disabled. Default true. */
  canFinalize?: boolean
  /**
   * What finalizing means here. 'send' (the default) is the wizard's own goal,
   * "Save & send invitations". 'save' is for a host reopening a plan whose
   * invitations already went out: it is saving an edit, not inviting anyone new.
   *
   * A mode rather than a label so the wording stays in this library's locale
   * bundles — a `string` prop would let a host ship English text into an
   * otherwise fully translated wizard.
   */
  finalizeMode?: 'send' | 'save'
}

export function PlanHuntWizardShell({
  steps, currentStepId,
  onStepSelect, onBack, onContinue, onCancel, onSaveDraft, onFinalize,
  saving = false, canSaveDraft = true, canFinalize = true, finalizeMode = 'send',
}: PlanHuntWizardShellProps) {
  const { t } = useTranslation('planHunt')
  const shellRef = useRef<HTMLDivElement>(null)
  const fitHeight = useViewportFitHeight(shellRef)
  const idx = steps.findIndex((s) => s.id === currentStepId)
  const current = steps[idx]
  const isFirstStep = idx === 0
  const isLastStep = idx === steps.length - 1

  // Sub-heading under the title, at the app's standard sub-heading size. The
  // flow used to invert this pairing as a small uppercase eyebrow above the
  // title, which read as a different product (CLUB-579).
  //
  // A step carries its own copy when a caller supplies one; otherwise its id is
  // looked up in the shared translated descriptions (`shell.stepDescription.*`,
  // keyed by the six plan-hunt step ids both callers use). An id with no entry
  // falls back to an empty string, leaving just the step counter.
  const description =
    current?.description ??
    (current ? t(`shell.stepDescription.${current.id}`, { defaultValue: '' }) : '')
  const subheading = [t('shell.stepCounter', { current: idx + 1, total: steps.length }), description]
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      ref={shellRef}
      className="wh-plan-wizard"
      style={{
        // First paint uses the old approximation; the measured value replaces
        // it before the user can interact. minHeight wins on tiny viewports
        // (the page scrolls there instead of crushing the wizard).
        height: fitHeight ?? 'calc(100vh - 56px - 48px - 32px)',
        minHeight: 560,
        background: COLORS.surfaceContainerLowest,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 32,
              fontWeight: 500,
              lineHeight: 1.1,
              margin: 0,
              color: COLORS.onSurface,
            }}
          >
            {current?.title ?? current?.label}
          </h1>
          <div style={{ marginTop: 6, fontSize: 14, color: COLORS.outline }}>{subheading}</div>
        </div>
        <button className="wh-btn-icon" type="button" onClick={onCancel} disabled={saving} aria-label={t('shell.close')}>
          <Icon name="close" size={18} />
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '20px 24px' }}>
        {current?.content}
      </div>

      {/* Step rail */}
      <div
        className="wh-plan-rail"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 24px',
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.primaryContainer,
          flexShrink: 0,
          gap: 0,
        }}
      >
        <RailNavButton
          dir="back"
          onClick={onBack}
          disabled={isFirstStep || saving}
        />
        {steps.map((s, i) => {
          const done = s.state === 'done'
          const active = s.state === 'active'
          const mustMissing = s.state === 'locked'
          const isLastRailStep = i === steps.length - 1
          const navigable = !!onStepSelect
          return (
            <Fragment key={s.id}>
              <button
                type="button"
                data-state={s.state}
                // The circle renders a bare "2" or "!" next to the label, which
                // would otherwise be read out as part of the button's name
                // ("2 Leaders") and change as the step's state does. Naming the
                // button after the step keeps it stable for both screen readers
                // and tests.
                aria-label={s.label}
                aria-current={active ? 'step' : undefined}
                className="wh-plan-rail-step"
                onClick={navigable ? () => onStepSelect!(i) : undefined}
                disabled={!navigable}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 6px',
                  border: 'none',
                  background: 'transparent',
                  cursor: navigable ? 'pointer' : 'default',
                  minWidth: 72,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    flexShrink: 0,
                    background: done
                      ? COLORS.primary
                      : active
                      ? COLORS.surfaceContainerLowest
                      : 'transparent',
                    border: `2px solid ${
                      active
                        ? COLORS.primary
                        : done
                        ? 'transparent'
                        : mustMissing
                        ? COLORS.onTertiaryFixedVariant
                        : COLORS.outlineVariant
                    }`,
                    color: done
                      ? COLORS.onPrimary
                      : active
                      ? COLORS.primary
                      : mustMissing
                      ? COLORS.onTertiaryFixedVariant
                      : COLORS.outline,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {done ? <Icon name="check" size={13} /> : mustMissing ? '!' : i + 1}
                </span>
                <span
                  className="wh-plan-rail-label"
                  style={{
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    color: active
                      ? COLORS.primary
                      : done
                      ? COLORS.onSurface
                      : mustMissing
                      ? COLORS.onTertiaryFixedVariant
                      : COLORS.outline,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </span>
              </button>
              {!isLastRailStep && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    marginBottom: 18,
                    background: done ? COLORS.primary : COLORS.outlineVariant,
                    opacity: done ? 0.45 : 0.25,
                  }}
                />
              )}
            </Fragment>
          )
        })}
        <RailNavButton
          dir="continue"
          onClick={onContinue}
          disabled={isLastStep || saving || !current?.canContinue}
          primary
        />
      </div>

      {/* Footer actions */}
      <div
        className="wh-plan-wizard-footer"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 24px',
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
          flexShrink: 0,
        }}
      >
        <button className="wh-btn wh-btn-text" type="button" onClick={onCancel} disabled={saving}>
          {t('shell.cancel')}
        </button>
        {/* Hidden, not disabled, when finalizing means saving an edit: a plan
            whose invitations already went out has no draft to save, and the
            button's handler navigates away — so leaving it clickable loses the
            user's edit, and leaving it greyed out is a permanently dead control.
            Reopening a DRAFT still finalizes as 'send', so it keeps its button. */}
        {finalizeMode !== 'save' && (
          <button
            className="wh-btn wh-btn-outline"
            type="button"
            onClick={onSaveDraft}
            disabled={saving || !canSaveDraft}
            style={{ opacity: canSaveDraft ? 1 : 0.55 }}
            title={canSaveDraft ? t('shell.saveDraftTitle') : t('shell.selectTeamFirst')}
          >
            <Icon name="check" size={15} />
            {t('shell.saveDraft')}
          </button>
        )}
        <div className="wh-plan-footer-spacer" style={{ flex: 1 }} />
        {/* "Save & send invitations" is always visible so the goal of the
            wizard stays in view, but disabled (via canFinalize) until every
            required detail is provided — the tooltip says what's missing. */}
        <button
          className="wh-btn wh-btn-primary wh-plan-send-btn"
          type="button"
          onClick={onFinalize}
          disabled={!onFinalize || saving || !canFinalize}
          style={{ opacity: canFinalize ? 1 : 0.55 }}
          title={!onFinalize ? t('shell.sendUnavailable') : !canFinalize ? t('shell.sendMissingDetails') : undefined}
        >
          <Icon name="send" size={16} />
          {finalizeMode === 'save' ? t('shell.saveChanges') : t('shell.sendInvitations')}
        </button>
      </div>
    </div>
  )
}

/**
 * Back / Continue arrows that flank the step rail. `marginBottom` lifts them to
 * align with the step circles (which sit above their labels), matching the
 * connector lines. The Continue arrow is the primary forward action.
 */
function RailNavButton({
  dir,
  onClick,
  disabled,
  primary,
}: {
  dir: 'back' | 'continue'
  onClick: () => void
  disabled?: boolean
  primary?: boolean
}) {
  const { t } = useTranslation('planHunt')
  const isBack = dir === 'back'
  const label = isBack ? t('shell.back') : t('shell.continue')
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        width: 34,
        height: 34,
        borderRadius: 999,
        flexShrink: 0,
        marginBottom: 18,
        marginRight: isBack ? 8 : 0,
        marginLeft: isBack ? 0 : 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: primary ? 'none' : `1.5px solid ${COLORS.outlineVariant}`,
        background: primary ? COLORS.primary : COLORS.surfaceContainerLowest,
        color: primary ? COLORS.onPrimary : COLORS.onSurface,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        boxShadow: primary && !disabled ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
      }}
    >
      <Icon name={isBack ? 'chevron-left' : 'chevron-right'} size={18} />
    </button>
  )
}
